/**
 * The Work search's match rules, one definition for both halves: the list
 * route builds its SQL from `parseWorkSearch`, and the Work list reads
 * `workSearchReasonOf` to say why a row matched (which text to mark, whether
 * to print the PR or the branch).
 *
 * The whole query decides its shape:
 *   NUMBER  "2270" / "#2270"  → a PR number, or the number in our id or in the
 *                               tracker key. Whole numbers only.
 *   KEY     "ENG-804"          → our id or the tracker key, exactly; or the key
 *                               as a whole word of the PR branch.
 *   TEXT    anything else      → every word is somewhere in the title.
 */
export const WORK_SEARCH_MIN_LENGTH = 2;
export const WORK_SEARCH_MAX_LENGTH = 200;
/** The server answers with at most this many rows; one more says "there are more". */
export const WORK_SEARCH_LIMIT = 200;
/** Extra words past this are dropped: a sentence is not a search. */
const MAX_WORDS = 8;
// Nine digits stay inside a Postgres integer; a longer run is searched as text.
const NUMBER_RE = /^#?(\d{1,9})$/;
const KEY_RE = /^[a-z]{2,6}-\d{1,9}$/i;
export function parseWorkSearch(raw) {
    const q = raw.trim().slice(0, WORK_SEARCH_MAX_LENGTH);
    if (q.length < WORK_SEARCH_MIN_LENGTH)
        return null;
    const number = NUMBER_RE.exec(q);
    if (number)
        return { shape: "number", number: Number(number[1]) };
    if (KEY_RE.test(q))
        return { shape: "key", key: q.toUpperCase() };
    const words = [...new Set(q.toLowerCase().split(/\s+/).filter(Boolean))].slice(0, MAX_WORDS);
    return { shape: "text", words };
}
/**
 * The key as a whole word of a branch name: `eng-804` is in
 * `bruno/eng-804-plan`, but not in `eng-8040` or `xeng-804`. The key is
 * already validated by `KEY_RE`, so it is safe inside a pattern.
 */
export function branchHasKey(branch, key) {
    return new RegExp(`(^|[^a-z0-9])${key.toLowerCase()}([^0-9]|$)`).test(branch.toLowerCase());
}
/** The number at the end of a key (`ENG-42` → 42), or null when there is none. */
export function keyNumberOf(key) {
    const tail = key?.split("-").pop();
    return tail && /^\d+$/.test(tail) ? Number(tail) : null;
}
/**
 * The text a matched key or number shows as, to mark in a row: the number for
 * a number search, the key for a key search. A text search marks its words.
 */
export function searchNeedleOf(query) {
    if (query.shape === "number")
        return String(query.number);
    if (query.shape === "key")
        return query.key;
    return "";
}
/** Why `subject` matches `query`, or null when it does not. */
export function workSearchReasonOf(subject, query) {
    switch (query.shape) {
        case "number":
            if (subject.prNumber === query.number)
                return "pr";
            if (keyNumberOf(subject.identifier) === query.number)
                return "key";
            if (keyNumberOf(subject.trackerKey) === query.number)
                return "key";
            return null;
        case "key":
            if (subject.identifier.toUpperCase() === query.key)
                return "key";
            if (subject.trackerKey?.toUpperCase() === query.key)
                return "key";
            if (subject.prBranch && branchHasKey(subject.prBranch, query.key))
                return "branch";
            return null;
        case "text": {
            const title = subject.title.toLowerCase();
            return query.words.every((word) => title.includes(word)) ? "title" : null;
        }
    }
}
//# sourceMappingURL=workSearch.js.map